import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { api } from "@/lib/apiClient";
import { useBookingRealtimeRefresh } from "@/lib/useBookingRealtimeRefresh";
import { useCurrentUser } from "@/lib/auth";
import {
  consecutiveDaysFrom,
  formatArrivalDayChip,
  formatArrivalDayLong,
  todayLocal,
} from "@/lib/bookingDates";
import { canWriteGuestPitchBooking } from "@/lib/permissions";
import { usePageHeader } from "@/layout/usePageHeader";
import { ArrivalsTable } from "@/components/bookings/ArrivalsTable";
import { useGuestById } from "@/lib/useGuestById";
import type { ArrivalDayCount, Booking, Guest, Pitch, PitchCategory } from "@/types/camping";
import { EditBookingModal } from "@/pages/bookings/EditBookingModal";
import { CheckInFlowModal } from "@/components/bookings/CheckInFlowModal";
import { OpenedInvoiceDraftModal } from "@/components/invoices/OpenedInvoiceDraftModal";
import { readGuestTabletWorkstationCode } from "@/lib/guestTabletWorkstationStorage";
import { GuestStayModal } from "@/components/bookings/GuestStayModal";
import { EditGuestModal } from "@/pages/guests/EditGuestModal";

const ARRIVAL_DAY_COUNT = 7;

function canCheckIn(status: string): boolean {
  return status === "confirmed" || status === "reserved" || status === "requested";
}

export function ArrivalsPage() {
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canEdit = canWriteGuestPitchBooking(groups);
  const today = todayLocal();
  const dayOptions = useMemo(() => consecutiveDaysFrom(today, ARRIVAL_DAY_COUNT), [today]);

  const [selectedDate, setSelectedDate] = useState(today);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [pitches, setPitches] = useState<Pitch[]>([]);
  const [categories, setCategories] = useState<PitchCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkInFlowBooking, setCheckInFlowBooking] = useState<Booking | null>(null);
  const [stayModalBooking, setStayModalBooking] = useState<Booking | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [editGuest, setEditGuest] = useState<Guest | null>(null);
  const [openedInvoiceId, setOpenedInvoiceId] = useState<string | null>(null);
  const [arrivalCounts, setArrivalCounts] = useState<Record<string, number>>({});
  const [sendingComfortId, setSendingComfortId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const isToday = selectedDate === today;
  const canCheckInToday = isToday;

  const load = useCallback((options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true);
    setError(null);
    Promise.all([
      api.get<Booking[]>(`/api/v1/bookings/arrivals?arrival_date=${selectedDate}`),
      api.get<Pitch[]>("/api/v1/pitches"),
      api.get<PitchCategory[]>("/api/v1/pitch-categories").catch(() => [] as PitchCategory[]),
    ])
      .then(([b, p, c]) => {
        setBookings(b);
        setPitches(p);
        setCategories(c);
        setStayModalBooking((current) =>
          current ? b.find((row) => row.id === current.id) ?? current : current,
        );
        setSelectedBooking((current) =>
          current ? b.find((row) => row.id === current.id) ?? current : current,
        );
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [selectedDate]);

  useEffect(() => {
    if (!dayOptions.includes(selectedDate)) {
      setSelectedDate(today);
    }
  }, [dayOptions, selectedDate, today]);

  const loadCounts = useCallback(() => {
    api
      .get<ArrivalDayCount[]>(
        `/api/v1/bookings/arrivals/summary?from=${today}&days=${ARRIVAL_DAY_COUNT}`,
      )
      .then((rows) => {
        const map: Record<string, number> = {};
        for (const row of rows) {
          map[row.arrival_date] = row.count;
        }
        setArrivalCounts(map);
      })
      .catch(() => {
        /* Zähler sind optional – Tagesliste bleibt nutzbar */
      });
  }, [today]);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  useEffect(() => {
    setNotice(null);
  }, [selectedDate]);

  useBookingRealtimeRefresh(() => {
    load({ silent: true });
    loadCounts();
  });

  useEffect(() => {
    const title = isToday ? "Anreisen heute" : "Anreisen";
    const subtitle = isToday
      ? `Ausstehende Anreisen am ${formatArrivalDayLong(today)}`
      : `Ausstehende Anreisen am ${formatArrivalDayLong(selectedDate)}`;
    setPageMeta({ title, subtitle });
    load();
    return () => setPageMeta(null);
  }, [isToday, load, selectedDate, setPageMeta, today]);

  const stayModalGuest = useGuestById(stayModalBooking?.guest_id);

  const stayModalPitch = useMemo(
    () =>
      stayModalBooking?.pitch_id
        ? pitches.find((p) => p.id === stayModalBooking.pitch_id) ?? null
        : null,
    [stayModalBooking, pitches],
  );

  const selectedBookingGuest = useGuestById(selectedBooking?.guest_id);

  const introText = isToday
    ? "Noch nicht eingecheckt. „Comfort Check-in“ schickt dem Gast eine E-Mail für Kennzeichen und Online-Zahlung. „Einchecken“ startet den Check-in an der Rezeption."
    : "Noch nicht eingecheckt. „Comfort Check-in“ schickt dem Gast eine E-Mail für Kennzeichen und Online-Zahlung. Check-in an der Rezeption ist erst am Anreisetag möglich.";

  const emptyMessage = isToday
    ? "Keine ausstehenden Anreisen für heute."
    : `Keine ausstehenden Anreisen am ${formatArrivalDayLong(selectedDate)}.`;

  async function sendComfortCheckIn(booking: Booking) {
    if (!canEdit || !canCheckIn(booking.status) || booking.comfort_check_in_completed) return;
    setSendingComfortId(booking.id);
    setError(null);
    setNotice(null);
    try {
      const result = await api.post<{ sent_to: string; booking: Booking }>(
        `/api/v1/bookings/${booking.id}/comfort-check-in/send`,
      );
      setBookings((rows) => rows.map((row) => (row.id === result.booking.id ? result.booking : row)));
      setNotice(`Comfort-Check-in-Mail an ${result.sent_to} gesendet.`);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Comfort-Check-in-Mail konnte nicht gesendet werden.");
    } finally {
      setSendingComfortId(null);
    }
  }

  function startCheckInFlow(booking: Booking) {
    if (!canEdit || !canCheckInToday || !canCheckIn(booking.status)) return;
    setError(null);
    setCheckInFlowBooking(booking);
  }

  const checkInFlowGuest = useGuestById(checkInFlowBooking?.guest_id);

  const checkInFlowPitch = useMemo(
    () =>
      checkInFlowBooking?.pitch_id
        ? pitches.find((p) => p.id === checkInFlowBooking.pitch_id) ?? null
        : null,
    [checkInFlowBooking, pitches],
  );

  return (
    <div className="space-y-6">
      <div className="space-y-3" data-tour="arrivals-day-picker">
        <p className="text-sm font-medium text-label">Tag wählen</p>
        <div className="overflow-x-auto pb-1">
          <div
            className="eh-segmented eh-segmented--md min-w-max"
            role="tablist"
            aria-label="Anreisetag wählen"
          >
            {dayOptions.map((day) => {
              const count = arrivalCounts[day] ?? 0;
              return (
                <button
                  key={day}
                  type="button"
                  role="tab"
                  aria-selected={day === selectedDate}
                  className={clsx(
                    "eh-segmented-item flex flex-col items-center gap-0.5 py-2",
                    day === selectedDate && "is-active",
                  )}
                  onClick={() => setSelectedDate(day)}
                >
                  <span>{formatArrivalDayChip(day, today)}</span>
                  <span
                    className={clsx("eh-stay-day-count", count > 0 && "is-hot")}
                    aria-label={`${count} Anreisen`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <p className="text-sm text-label-secondary">{introText}</p>

      <CheckInFlowModal
        open={!!checkInFlowBooking}
        booking={checkInFlowBooking}
        guest={checkInFlowGuest}
        pitch={checkInFlowPitch}
        userGroups={groups}
        guestTabletWorkstationCode={
          typeof localStorage !== "undefined"
            ? readGuestTabletWorkstationCode()
            : null
        }
        onClose={() => setCheckInFlowBooking(null)}
        onCompleted={(updated) => {
          load();
          loadCounts();
          setCheckInFlowBooking(null);
          setStayModalBooking(updated);
        }}
        onDraftAborted={(invoiceId, updated) => {
          load();
          loadCounts();
          setStayModalBooking(updated);
          setOpenedInvoiceId(invoiceId);
        }}
      />

      <OpenedInvoiceDraftModal
        invoiceId={openedInvoiceId}
        open={!!openedInvoiceId}
        userGroups={groups}
        onClose={() => setOpenedInvoiceId(null)}
        onChanged={() => {
          load();
          loadCounts();
        }}
      />

      <GuestStayModal
        open={!!stayModalBooking}
        booking={stayModalBooking}
        guest={stayModalGuest}
        pitch={stayModalPitch}
        canEdit={canEdit}
        userGroups={groups}
        onInvoiceChanged={() => {
          load();
          loadCounts();
        }}
        onClose={() => setStayModalBooking(null)}
        onStartCheckIn={
          stayModalBooking && canCheckInToday && canCheckIn(stayModalBooking.status)
            ? () => {
                startCheckInFlow(stayModalBooking);
                setStayModalBooking(null);
              }
            : undefined
        }
        onEditBooking={() => {
          if (stayModalBooking) {
            setSelectedBooking(stayModalBooking);
            setStayModalBooking(null);
          }
        }}
        onEditGuest={() => {
          if (stayModalGuest) {
            setEditGuest(stayModalGuest);
            setStayModalBooking(null);
          }
        }}
      />

      <EditBookingModal
        open={!!selectedBooking}
        booking={selectedBooking}
        guest={selectedBookingGuest}
        pitches={pitches}
        onClose={() => setSelectedBooking(null)}
        onSaved={() => {
          load();
          loadCounts();
        }}
        canEdit={canEdit}
        onOpenGuest={(g) => setEditGuest(g)}
      />

      {editGuest ? (
        <EditGuestModal
          open
          guest={editGuest}
          canEdit={canEdit}
          onClose={() => setEditGuest(null)}
          onSaved={() => {
            setEditGuest(null);
            load();
            loadCounts();
          }}
        />
      ) : null}

      {notice ? <p className="text-sm text-status-green">{notice}</p> : null}
      {error && <p className="text-sm text-status-red">{error}</p>}
      {loading ? (
        <p className="text-sm text-label-secondary">Lade Anreisen…</p>
      ) : (
        <div data-tour="arrivals-table">
          <ArrivalsTable
            bookings={bookings}
            pitches={pitches}
            categories={categories}
            canEdit={canEdit}
            canCheckInToday={canCheckInToday}
            listKey={selectedDate}
            emptyMessage={emptyMessage}
            onOpen={setStayModalBooking}
            onCheckIn={startCheckInFlow}
            onSendComfortCheckIn={(booking) => {
              void sendComfortCheckIn(booking);
            }}
            sendingComfortCheckInId={sendingComfortId}
          />
        </div>
      )}
    </div>
  );
}
